/**
 * Shared panel rendering utilities for consistent rendering across panel types.
 *
 * This module provides reusable rendering functions for panels, backsplashes, and similar
 * rectangular elements that share common rendering patterns:
 * - Rectangle to SVG polygon conversion
 * - Texture/material rendering
 * - Context menu integration
 * - Dimension line rendering
 * - Breakpoint rendering
 *
 * Architecture:
 * - Panels (including Backsplashes which extend Panel) use these utilities
 * - Identification is by panel.id and panel.label, NOT PanelType enum
 */

import { Axis, Breakpoint, BlueprintInstanceType, Vertex3 } from '@rta/rta-blueprint-library';
import { convertRect2Vectors } from '@r-core/helpers';
import { Layer } from '@r-core/types';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { addContextMenu, ReferenceEntryType } from '../../context-menu';
import { DimensionLine } from '@r-components';
import { RenderBreakpoint } from './render-breakpoint';

/**
 * Options for rendering a basic panel rectangle
 */
export type RenderPanelRectOptions = {
	/** Width of the panel */
	width: number;
	/** Height of the panel */
	height: number;
	/** Center position of the panel */
	center: { x: number; y: number };
	/** Rotation angle (default 0) */
	angle?: number;
	/** Layer for z-ordering (default Layer.Panel) */
	layer?: Layer;
	/** Panel instance type for context menu */
	instanceType: BlueprintInstanceType | ReferenceEntryType;
	/** ID for the panel (used in context menu and keys) */
	id: string;
	/** SVG-space center for context menu relative-position math (defaults to center) */
	contextCenter?: { x: number; y: number };
	/** Fill color or pattern (e.g., 'darkgray' or 'url(#pattern-id)') */
	fill: string;
	/** Stroke color (default 'black') */
	stroke?: string;
	/** Stroke width (default '0.125') */
	strokeWidth?: string;
	/** Opacity (default 1) */
	opacity?: number;
};

/**
 * Renders a basic panel rectangle with context menu support.
 * This is the foundation for rendering panels, backsplashes, and similar rectangular elements.
 *
 * @param options - Configuration for the panel rectangle
 * @returns React element containing the SVG polygon
 */
export const renderPanelRect = (options: RenderPanelRectOptions): React.ReactElement => {
	const {
		width,
		height,
		center,
		angle = 0,
		layer = Layer.Panel,
		instanceType,
		id,
		contextCenter = center,
		fill,
		stroke = 'black',
		strokeWidth = '0.125',
		opacity = 1
	} = options;

	const vectors = convertRect2Vectors({
		layer,
		center,
		width,
		height,
		angle
	});

	return svgPolygonFromVectors(vectors, {
		key: `panel-rect-${id}`,
		fill,
		stroke,
		strokeWidth,
		opacity,
		...addContextMenu({ menuKey: instanceType, id, center: contextCenter })
	});
};

/**
 * Options for rendering panel dimensions
 */
export type RenderPanelDimensionsOptions = {
	/** Width of the panel */
	width: number;
	/** Height of the panel */
	height: number;
	/** Center position for dimension calculation */
	center: { x: number; y: number };
	/** Whether to show the dimension label box */
	includeLabelBox: boolean;
	/** Breakpoints that split the width dimension */
	breakpoints?: Breakpoint[];
	/** Whether to show breakpoints */
	showBreakpoints?: boolean;
	/** Padding offset for dimension lines */
	paddingTop?: number;
	/** Padding offset for side dimension */
	paddingSide?: number;
	/** Whether this is the first panel in a row (shows side dimension) */
	isFirst?: boolean;
};

/**
 * Renders dimension lines for a panel, including split dimensions at breakpoints.
 *
 * @param options - Configuration for panel dimensions
 * @returns React element containing dimension lines
 */
export const renderPanelDimensions = (options: RenderPanelDimensionsOptions): React.ReactElement => {
	const {
		width,
		height,
		center,
		includeLabelBox,
		breakpoints = [],
		showBreakpoints = false,
		paddingTop = -1.5,
		paddingSide = 0,
		isFirst = false
	} = options;

	const sortedBreakpoints = [...breakpoints].sort((a, b) => a.center - b.center);
	const hasBreakpoints = sortedBreakpoints.length > 0 && showBreakpoints;

	// Calculate dimension segments split by breakpoints
	const dimensionSegments: Array<{ xOffset: number; segmentWidth: number }> = [];
	if (hasBreakpoints) {
		let prevPosition = -(width / 2);
		sortedBreakpoints.forEach((bp) => {
			const segmentWidth = bp.center - prevPosition;
			dimensionSegments.push({
				xOffset: prevPosition + (segmentWidth / 2),
				segmentWidth
			});
			prevPosition = bp.center;
		});
		// Add final segment from last breakpoint to right edge
		const finalWidth = (width / 2) - prevPosition;
		dimensionSegments.push({
			xOffset: prevPosition + (finalWidth / 2),
			segmentWidth: finalWidth
		});
	}

	return (
		<>
			{/* Width dimensions - split at breakpoints if present */}
			{hasBreakpoints ? (
				// Render total dimension above breakpoint segments, plus individual segment dimensions
				<>
					{/* Total width dimension line (further from panel) */}
					<DimensionLine
						length={width}
						axis={new Axis({ dataIn: { x: 0, y: 0, z: 0 }})}
						includeLabelBox={includeLabelBox}
						bBox={{
							origin: new Vertex3({ dataIn: { x: center.x, y: center.y - (height / 2), z: 0 }}),
							size: { width, height: 0, depth: 0 }
						}}
						padding={paddingTop - 2.5}
						indicatorStart="flat"
						indicatorEnd="flat"
						level={true}
					/>
					{/* Individual breakpoint segment dimension lines (closer to panel) */}
					{dimensionSegments.map((segment, segIndex) => (
						<DimensionLine
							key={`dim-segment-${segIndex}`}
							length={segment.segmentWidth}
							axis={new Axis({ dataIn: { x: 0, y: 0, z: 0 }})}
							includeLabelBox={includeLabelBox}
							bBox={{
								origin: new Vertex3({ dataIn: { x: center.x + segment.xOffset, y: center.y - (height / 2), z: 0 }}),
								size: { width: segment.segmentWidth, height: 0, depth: 0 }
							}}
							padding={paddingTop}
							indicatorStart="flat"
							indicatorEnd="flat"
							level={true}
						/>
					))}
				</>
			) : (
				// No breakpoints - single dimension line for entire width
				<DimensionLine
					length={width}
					axis={new Axis({ dataIn: { x: 0, y: 0, z: 0 }})}
					includeLabelBox={includeLabelBox}
					bBox={{
						origin: new Vertex3({ dataIn: { x: center.x, y: center.y - (height / 2), z: 0 }}),
						size: { width, height: 0, depth: 0 }
					}}
					padding={paddingTop}
					indicatorStart="flat"
					indicatorEnd="flat"
					level={true}
				/>
			)}

			{/* Height dimension (vertical - left side of panel) - only show for first in row */}
			{isFirst && (
				<DimensionLine
					length={height}
					axis={new Axis({ dataIn: { x: 0, y: 0, z: 90 }})}
					includeLabelBox={includeLabelBox}
					bBox={{
						origin: new Vertex3({ dataIn: { x: center.x - (width / 2) - 2, y: center.y, z: 0 }}),
						size: { width: 0, height, depth: 0 }
					}}
					padding={paddingSide}
					indicatorStart="flat"
					indicatorEnd="flat"
					level={true}
				/>
			)}
		</>
	);
};

/**
 * Options for rendering panel breakpoints
 */
export type RenderPanelBreakpointsOptions = {
	/** Breakpoints to render */
	breakpoints: Breakpoint[];
	/** Height of the panel (for breakpoint visualization) */
	height: number;
	/** Whether breakpoints should be shown */
	showBreakpoints: boolean;
};

/**
 * Renders breakpoints for a panel.
 *
 * @param options - Configuration for breakpoint rendering
 * @returns React element containing breakpoint visualizations
 */
export const renderPanelBreakpoints = (options: RenderPanelBreakpointsOptions): React.ReactElement => {
	const { breakpoints, height, showBreakpoints } = options;

	if (!showBreakpoints || breakpoints.length === 0) {
		return <></>;
	}

	return (
		<>
			{breakpoints.map((breakpoint) => (
				<RenderBreakpoint
					key={breakpoint.id}
					breakpoint={breakpoint}
					center={{ x: breakpoint.center, y: 0 }}
					height={height}
				/>
			))}
		</>
	);
};

/**
 * Options for creating a texture pattern definition
 */
export type CreateTexturePatternDef = {
	/** Unique ID for the pattern */
	patternId: string;
	/** URL to the texture image */
	textureUrl: string;
	/** Width of the texture pattern */
	width: number;
	/** Height of the texture pattern */
	height: number;
};

/**
 * Creates an SVG pattern definition for a texture.
 * Place this in a <defs> element.
 *
 * @param options - Configuration for the texture pattern
 * @returns React element containing the pattern definition
 */
export const createTexturePattern = (options: CreateTexturePatternDef & { key?: string }): React.ReactElement => {
	const { patternId, textureUrl, width, height, key } = options;

	return (
		<pattern
			key={key ?? patternId}
			id={patternId}
			x="0"
			y="0"
			height={height}
			width={width}
			patternUnits="userSpaceOnUse"
		>
			<image
				href={textureUrl}
				x="0"
				y="0"
				width={width}
				height={height}
			/>
		</pattern>
	);
};
